// janado motion runtime: smooth scroll (Lenis, on trial), the cursor blob and the page transition (leaving side).
// The arrival side lives in layout/theme.liquid (cover before first paint) and assets/blob-field.js (flow back).

const html = document.documentElement
const reduced = html.classList.contains('reduced')
const fine = html.classList.contains('fine')
const j = (window.janado = window.janado || {})
const whenField = (fn) => (j.field ? fn(j.field) : addEventListener('janado:field', () => fn(j.field), { once: true }))

// ---------- Smooth scroll (Lenis, wheel only: touch keeps the native feel) ----------
// On trial since 2026-10-01: remove this block if it hurts phones or performance.
// lenis.min.js is a deferred script placed before this module, so it has run by now.
if (!reduced && window.Lenis) j.lenis = new window.Lenis({ lerp: 0.1, autoRaf: true })

/** Scroll to an element or y position, smooth through Lenis when it is there. */
j.scrollTo = (target, opts = {}) => {
  if (j.lenis) return j.lenis.scrollTo(target, { duration: 1.2, ...opts })
  const y = typeof target === 'number' ? target : target.getBoundingClientRect().top + scrollY + (opts.offset || 0)
  scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' })
}

// ---------- Cursor blob: a small drop that follows the pointer and merges with everything it touches ----------
if (fine && !reduced)
  whenField((field) => {
    field.setCursor(true)
    let x = -99
    let y = -99
    let speed = 0
    addEventListener('pointermove', (e) => {
      speed = Math.min(Math.hypot(e.clientX - x, e.clientY - y), 60)
      x = e.clientX
      y = e.clientY
    }, { passive: true })
    html.addEventListener('pointerleave', () => (field.mouse.active = false))
    const tick = () => {
      speed *= 0.9
      field.cursorTarget(x, y, field.mouse.active ? 9 + speed * 0.35 : 0)
      requestAnimationFrame(tick)
    }
    tick()
  })

// ---------- Page transition, leaving side ----------
// A click on an internal link floods the screen with a blob, then navigates. The blob of a product card
// (data-blob inside the link) is the one that floods, so it can land on the product page with the same id.
// Opt out per link with data-no-flood; set the colour with data-flood="lavender" or let it come from the blob.
const skip = (a, e) =>
  e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
  a.target && a.target !== '_self' || a.hasAttribute('download') || a.hasAttribute('data-no-flood') ||
  a.origin !== location.origin || /^\/(checkout|cart\/|account\/logout)/.test(a.pathname) ||
  (a.pathname === location.pathname && a.search === location.search) // same page (incl. #anchors)

let leaving = false
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href]')
  if (!a || reduced || leaving || skip(a, e)) return
  const field = j.field
  const own = a.querySelector('[data-blob]')
  const id = own ? own.dataset.blob : 'flood-' + Date.now()
  const c = a.dataset.flood || (own || a).closest('[data-blob-color]')?.dataset.blobColor || 'yellow'
  try {
    sessionStorage.setItem('janado:flood', JSON.stringify({ id, c, t: Date.now() }))
  } catch (err) {
    return // no storage, no arrival: just navigate normally
  }
  e.preventDefault()
  leaving = true
  const go = () => location.assign(a.href)
  html.classList.add('leaving')
  if (!field) return setTimeout(go, 250)
  // a CSS-drawn card blob (data-blob-css): the flood starts exactly on it and takes over
  let from = { x: e.clientX, y: e.clientY }
  if (!fine && own?.hasAttribute('data-blob-css')) {
    const r = own.getBoundingClientRect()
    from = { x: r.left + r.width / 2, y: r.top + r.height / 2, r: Math.min(r.width, r.height) / 2 }
  }
  // Navigate once the flood covers the screen. Starting earlier makes a prefetched page (speculation rules in the
  // layout) arrive while the flood is only half grown: the new page then pops in fully yellow, a visible jump.
  field.flood(id, from, c).then(go)
})

// Back/forward cache: the page we left comes back as it was, without the flood.
addEventListener('pageshow', (e) => {
  if (!e.persisted) return
  leaving = false
  if (!j.field) html.classList.remove('leaving') // with the field, assets/blob-field.js animates the way back
})

// ---------- Tilt + sheen for device photos ([data-tilt], zone = closest [data-tilt-zone]) ----------
// Mouse: the device turns toward the pointer and the light follows it. Touch: no tilt, a sheen sweeps across by
// itself every few seconds while it's on screen. The sheen is masked with the photo itself, so it only lights the
// device. Variant swaps change the same <img>, its load event re-points the mask.
if (!reduced) {
  const masks = CSS.supports('mask-image', 'url("x.png")') || CSS.supports('-webkit-mask-image', 'url("x.png")')
  // [data-tilt-touch] (product cards): only the automatic sheen on touch devices, nothing with a mouse
  for (const el of document.querySelectorAll(fine ? '[data-tilt]' : '[data-tilt], [data-tilt-touch]')) {
    const img = el.querySelector('img')
    if (!img || !masks) continue
    const sheen = document.createElement('span')
    sheen.className = 'tilt__sheen'
    sheen.setAttribute('aria-hidden', 'true')
    el.append(sheen)
    const setMask = () => sheen.style.setProperty('--mask', `url("${img.currentSrc || img.src}")`)
    setMask()
    el.addEventListener('load', setMask, true)
    if (!fine) {
      el.classList.add('tilt--auto')
      continue
    }
    const zone = el.closest('[data-tilt-zone]') || el.parentElement
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0
    const step = () => {
      x += (tx - x) * 0.08
      y += (ty - y) * 0.08
      el.style.transform = `perspective(900px) rotateX(${(-y * 16).toFixed(2)}deg) rotateY(${(x * 22).toFixed(2)}deg)`
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.001 ? requestAnimationFrame(step) : 0
    }
    const go = () => raf || (raf = requestAnimationFrame(step))
    zone.addEventListener('pointermove', (e) => {
      const r = zone.getBoundingClientRect()
      tx = (e.clientX - r.left) / r.width - 0.5
      ty = (e.clientY - r.top) / r.height - 0.5
      el.classList.add('is-tilting')
      sheen.style.setProperty('--sx', `${50 + tx * 90}%`)
      go()
    })
    zone.addEventListener('pointerleave', () => ((tx = ty = 0), el.classList.remove('is-tilting'), go()))
  }
}

// ---------- Accordions: every <details> opens and closes smoothly (height + content), not with a jump ----------
// Same-name groups (only one open, e.g. the product page) animate the one that closes too. Opt out: data-no-acc.
const ACC_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const accBody = (d) => [...d.children].filter((c) => c.tagName !== 'SUMMARY')
function accClose(d) {
  if (!d.open || d.classList.contains('is-closing')) return
  const from = d.offsetHeight
  d.classList.add('is-closing')
  d.open = false
  const to = d.offsetHeight
  d.open = true
  d.style.overflow = 'clip'
  accBody(d).forEach((c) => c.animate([{ opacity: 1 }, { opacity: 0, transform: 'translateY(-6px)' }], { duration: 220, easing: 'ease-in', fill: 'forwards' }))
  d.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 380, easing: ACC_EASE }).onfinish = () => {
    d.open = false
    d.classList.remove('is-closing')
    d.style.overflow = ''
    accBody(d).forEach((c) => c.getAnimations().forEach((a) => a.cancel()))
  }
}
function accOpen(d) {
  const name = d.getAttribute('name')
  if (name) {
    // the browser would close the open sibling instantly; take the group apart while we animate
    const group = [...document.querySelectorAll(`details[name="${CSS.escape(name)}"]`)]
    group.forEach((g) => g.removeAttribute('name'))
    for (const g of group.filter((g) => g !== d && g.open)) {
      // An open sibling above would shrink while this one opens and pull it up out of view: close that one at
      // once and keep the clicked summary where it was (lenis or native scroll). Siblings below animate.
      if (g.compareDocumentPosition(d) & Node.DOCUMENT_POSITION_FOLLOWING) {
        const before = d.getBoundingClientRect().top
        g.getAnimations().forEach((a) => a.cancel())
        g.classList.remove('is-closing')
        g.style.overflow = ''
        accBody(g).forEach((c) => c.getAnimations().forEach((a) => a.cancel()))
        g.open = false
        const y = scrollY + d.getBoundingClientRect().top - before
        j.lenis ? j.lenis.scrollTo(y, { immediate: true, force: true }) : scrollTo(0, y)
      } else accClose(g)
    }
    setTimeout(() => group.forEach((g) => g.setAttribute('name', name)), 600)
  }
  const from = d.offsetHeight
  d.open = true
  const to = d.offsetHeight
  d.style.overflow = 'clip'
  d.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 560, easing: ACC_EASE }).onfinish = () => (d.style.overflow = '')
  accBody(d).forEach((c) => c.animate([{ opacity: 0, transform: 'translateY(-10px)' }, { opacity: 1, transform: 'none' }], { duration: 520, delay: 80, easing: ACC_EASE, fill: 'backwards' }))
}
if (!reduced)
  document.addEventListener('click', (e) => {
    const s = e.target.closest('summary')
    const d = s?.parentElement
    if (!(d instanceof HTMLDetailsElement) || d.closest('[data-no-acc]') || e.target.closest('a, button, input, label')) return
    e.preventDefault()
    d.open && !d.classList.contains('is-closing') ? accClose(d) : accOpen(d)
  })

// ---------- FAQ tabs (sections/faq.liquid; without JS all categories stay visible one after another) ----------
for (const faq of document.querySelectorAll('[data-faq]')) {
  const tabs = faq.querySelector('[data-faq-tabs]')
  if (!tabs) continue
  const buttons = [...tabs.querySelectorAll('[role=tab]')]
  const panels = [...faq.querySelectorAll('[role=tabpanel]')]
  const show = (i) => {
    buttons.forEach((b, k) => b.setAttribute('aria-selected', String(i === k)))
    panels.forEach((p, k) => {
      p.hidden = i !== k
      if (i === k && !reduced) p.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 450, easing: ACC_EASE })
    })
  }
  tabs.hidden = false
  faq.classList.add('faq--tabs')
  tabs.addEventListener('click', (e) => {
    const b = e.target.closest('[role=tab]')
    if (b) show(buttons.indexOf(b))
  })
  tabs.addEventListener('keydown', (e) => {
    const i = buttons.indexOf(document.activeElement)
    if (i < 0 || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return
    const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
    buttons[n].focus()
    show(n)
  })
  show(0)
}

// ---------- Product photos at one visual size (cards, product page, cart, buy bar, cross-sell, menu, search …) ----------
// The shop's photos carry very different empty margins (a tightly cut charger vs. a router with lots of air) and
// shapes (a watch band is 1:5), so "fit the image" makes devices look huge or tiny. A 64 px copy of each photo
// tells where the device really is; the photo is then moved and scaled inside its own box (object-fit: contain,
// any box shape) so every device gets about the same area: AREA of the box's smaller side squared, never taller
// than MAX_H of the box, wide devices up to 115 % wide. Measured once per photo path, remembered for the visit.
// The CSS for the boxes (--fit, no stretching by tall photos) is in assets/critical.css.
const FIT = '.card__img > img, .pdp__img > img, .buybar__thumb > img, .xs__img > img, .cart-line__img > img, .cart-xs__img > img, .mega__disc > img, .blog-product__img > img, .bfd__stage > img, .hw-rev__stage > img, .cond__img > img[data-cond-photo]'
{
  const KEY = 'janado:fit2'
  let memo = {}
  try {
    memo = JSON.parse(sessionStorage.getItem(KEY) || '{}')
  } catch {}
  const save = () => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(memo))
    } catch {}
  }
  const AREA = 0.7 // share of the box a device may cover
  const MAX_H = 0.92 // never taller than this share of the box
  const id = (src) => src.split('?')[0].replace(/^https?:/, '') // the whole path: file names repeat across products
  // bounding box of the visible pixels, as fractions of the photo: [x0, y0, x1, y1, width / height]
  const measure = (src) =>
    new Promise((res) => {
      const im = new Image()
      im.crossOrigin = 'anonymous'
      im.onload = () => {
        try {
          const w = 64
          const h = Math.max(1, Math.round((64 * im.naturalHeight) / im.naturalWidth))
          const c = document.createElement('canvas')
          c.width = w
          c.height = h
          const g = c.getContext('2d', { willReadFrequently: true })
          g.drawImage(im, 0, 0, w, h)
          const d = g.getImageData(0, 0, w, h).data
          let x0 = w, y0 = h, x1 = -1, y1 = -1
          for (let y = 0; y < h; y++)
            for (let x = 0; x < w; x++) {
              const i = (y * w + x) * 4
              if (d[i + 3] < 24 || (d[i] > 246 && d[i + 1] > 246 && d[i + 2] > 246)) continue // transparent or white
              if (x < x0) x0 = x
              if (x > x1) x1 = x
              if (y < y0) y0 = y
              if (y > y1) y1 = y
            }
          res(x1 < 0 ? null : [x0 / w, y0 / h, (x1 + 1) / w, (y1 + 1) / h, im.naturalWidth / im.naturalHeight])
        } catch {
          res(null)
        }
      }
      im.onerror = () => res(null)
      const u = new URL(src, location.href)
      u.searchParams.set('width', '64')
      im.src = u.href
    })
  const apply = (img, m) => {
    const W = img.offsetWidth
    const H = img.offsetHeight
    if (!m || !W || !H) return
    const [x0, y0, x1, y1, ratio] = m
    // the photo as object-fit: contain draws it inside the img box, in px
    const c = Math.min(W / ratio, H)
    const pw = ratio * c
    const ph = c
    const w = (x1 - x0) * pw
    const h = (y1 - y0) * ph
    const S = Math.min(W, H)
    const k = Math.min(((w > h ? 1.15 : 1) * W) / w, (MAX_H * H) / h, Math.sqrt((AREA * S * S) / (w * h)))
    // the device's centre to the box centre, then scale around it
    const cx = (W - pw) / 2 + ((x0 + x1) / 2) * pw - W / 2
    const cy = (H - ph) / 2 + ((y0 + y1) / 2) * ph - H / 2
    img.style.setProperty('--fit', `translate(${((-cx * k) / W) * 100}%, ${((-cy * k) / H) * 100}%) scale(${k.toFixed(3)})`)
    // on the box too, so a sheen over the photo (tilt in this file) or the wear overlay (condition sheet) moves with it
    img.parentElement.style.setProperty('--fit', img.style.getPropertyValue('--fit'))
  }
  const fit = (img) => {
    const src = img.currentSrc || img.src
    if (!src || img.dataset.fit === src) return
    img.dataset.fit = src
    const key = id(src)
    if (key in memo) return apply(img, memo[key])
    measure(src).then((m) => {
      memo[key] = m
      save()
      if (img.dataset.fit === src) apply(img, m)
    })
  }
  // a box that changes size (a sheet opening, a hidden photo becoming visible, a resized window) is fitted again
  const ro = new ResizeObserver((list) =>
    list.forEach(({ target: img }) => {
      const m = memo[id(img.currentSrc || img.src || '')]
      if (m) apply(img, m)
    })
  )
  const watch = (img) => {
    if (img.dataset.fitWatch) return
    img.dataset.fitWatch = '1'
    img.addEventListener('load', () => fit(img)) // also a new src later (variant swap on the product page)
    ro.observe(img)
    if (img.complete && img.naturalWidth) fit(img)
  }
  const scan = (root) => {
    if (root.matches?.(FIT)) watch(root)
    root.querySelectorAll?.(FIT).forEach(watch)
  }
  scan(document)
  new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && scan(n)))).observe(document.body, { childList: true, subtree: true })
}
